import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Animated from 'react-native-reanimated';
import { Button, Spacer, AppView, CardView, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, ButtonGroup, } from 'design-system';
import AddressInput from 'design-system/address/AddressInput';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import KeyboardAvoidingView from 'design-system/KeyboardAvoidingView';
import Amplitude from 'utils/amplitude';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useMarginBottom } from 'hooks/useMarginBottom';
import createStyleSheets from 'utils/createStyleSheets';
import { OnboardingFlow } from 'utils/types/navigation';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { selectPrimaryAddressForGB } from 'reducers/selectors';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { editJISAChildAddressAction } from 'features/invest/actions';
import { muteErrorAndResult, useFetchOld, useHeaderHeight } from 'hooks';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import useCreditScoreNavigation from 'features/credit-score/hooks/useCreditScoreNavigation';
import useNavigateToNextScreen from 'features/invest/hooks/useNavigateToNextScreen';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { postUserAddress } from 'actions';
import useMonevoNavigation from 'features/borrow/monevo/hooks/useMonevoNavigation';
import { setMonevoPreviousAddress } from 'features/borrow/monevo/reducers/monevoOnboarding';
import { isIOS } from '../../../constants';
import { getAmplitudeFlow } from './utils';
import { useNavigateIfAddressInLoginNav } from '../hooks/useNavigateIfAddressInLoginNav';
const AddressInputScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const dispatch = useAppDispatch();
    const { navigateToNextStep } = useMonevoNavigation();
    const colors = useColors();
    useNavigateIfAddressInLoginNav();
    const styles = useStyles(styleSet);
    const flow = route.params?.flow;
    const address = route.params?.address;
    const isAddingSecondaryAddress = route.params?.isAddingSecondaryAddress;
    const primaryAddressForGB = useAppSelector(selectPrimaryAddressForGB);
    const [, , fetching, fetch] = useFetchOld(muteErrorAndResult);
    const { continueAddress } = useCreditScoreNavigation();
    const navigateToNextScreen = useNavigateToNextScreen();
    const headerHeight = useHeaderHeight();
    const addressInputRef = useRef(null);
    useEffect(() => {
        Amplitude.logEvent('User Addresses Screen', {
            flow: getAmplitudeFlow(flow),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        navigation.setOptions({
            headerRight: () => (<HelpScreenButton articlesKey={
                /* eslint-disable-next-line no-nested-ternary */
                flow === OnboardingFlow.RentReporting
                    ? HELP_ARTICLES_KEY.RENT_REPORTING_ONBOARDING
                    : flow === OnboardingFlow.Pots
                        ? HELP_ARTICLES_KEY.POTS_ONBOARDING
                        : undefined}/>),
            headerTintColor: colors.elements.primary,
        });
    }, [colors.elements.primary, flow]);
    const handleContinue = useCallback(async () => {
        const result = await addressInputRef.current?.validateInputs();
        if (!result)
            return;
        const data = {
            countryIsoCode: 'GB',
            county: result.county,
            postcode: result.postcode,
            townOrCity: result.townOrCity,
            streetName: result.streetName,
            isPrimary: !primaryAddressForGB,
            buildingName: result.buildingName || '',
            buildingNumber: result.buildingNumber || '',
            subBuildingName: result.subBuildingName || '',
        };
        if (route.params?.flow === OnboardingFlow.JISA &&
            route.params?.childDetails) {
            if (route.params.accountId) {
                const { accountId } = route.params;
                const response = await fetch(editJISAChildAddressAction(accountId, data));
                if (response) {
                    navigation.popTo('ProductTransferReviewAddress', {
                        accountId,
                        address: data,
                    });
                }
                return;
            }
            dispatch({ type: 'SAVE_ONBOARDING_STATE', childAddress: data });
            navigateToNextScreen();
            return;
        }
        if (flow === OnboardingFlow.Invest ||
            flow === OnboardingFlow.Pots ||
            flow === OnboardingFlow.Wallet) {
            navigation.navigateDeprecated('InvestConfirmAddress', {
                flow,
                postData: data,
                skipPostAddress: false,
                shouldUpdateAddress: false,
                addressInDatabase: undefined,
            });
            return;
        }
        // Credit-score flow skips ConfirmAddressScreen — persist the address
        // and pop back to the right credit-score screen directly. The "change
        // KYC'd address" path is excluded because it falls through to
        // ChangeAddressProgressScreen via ConfirmAddressScreen.
        const { routes } = navigation.getState();
        const isChangingKycdAddress = routes.some((r) => r.name === 'ChangeAddressProgressScreen');
        const isCreditScoreFlow = routes.some((r) => r.name === 'CreditScoreOnboarding' ||
            r.name === 'CreditScoreReviewAddress' ||
            r.name === 'CreditScoreRegistration');
        if (isCreditScoreFlow && !isChangingKycdAddress) {
            continueAddress({ ...data, id: address?.id }, !!isAddingSecondaryAddress);
            return;
        }
        if (flow === OnboardingFlow.CreditCard) {
            const res = await fetch(postUserAddress(data));
            if (!res)
                return;
            /**
             * Hand back to the Monevo step table rather than naming the next
             * screen — what follows depends on what is still missing.
             */
            if (isAddingSecondaryAddress && res.address) {
                dispatch(setMonevoPreviousAddress(res.address.id));
                navigateToNextStep('choosePreviousAddress');
            }
            else {
                navigateToNextStep('reviewAddress');
            }
            return;
        }
        navigation.navigateDeprecated('ConfirmAddressScreen', {
            address: data,
            flow,
            isAddingSecondaryAddress,
        });
    }, [
        address,
        continueAddress,
        flow,
        isAddingSecondaryAddress,
        navigateToNextScreen,
        navigateToNextStep,
        primaryAddressForGB,
        route.params,
    ]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const screenDesc = useMemo(() => {
        if (flow === OnboardingFlow.Pots ||
            flow === OnboardingFlow.Invest ||
            flow === OnboardingFlow.Wallet) {
            return 'Please provide accurate information, you might need to verify this later';
        }
        if (flow === OnboardingFlow.CreditScore) {
            return 'Help us find your report by providing the information';
        }
        return 'We are required to ask this information for regulatory purposes';
    }, [flow]);
    return (<AppView withFrame={false}>
        <KeyboardAvoidingView style={styles.keyboardAvoidingView} keyboardVerticalOffset={headerHeight} behavior={isIOS ? 'padding' : undefined}>
          <Animated.ScrollView scrollEventThrottle={16} onScroll={scrollHandler} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={paddingBottom}>
            <ScreenInfoHeader subTitle={screenDesc} title="Please make sure all the information is filled in"/>
            <Spacer h={8}/>
            <CardView noMarginTop noMarginBottom>
              <AddressInput noExteriorView postCodeHandled ref={addressInputRef} defaultAddress={address} onSubmitEditing={handleContinue}/>
            </CardView>
          </Animated.ScrollView>
        </KeyboardAvoidingView>
        <ButtonGroup type="floating" margins>
          <Button brand title="Continue" isFetching={fetching} onPress={handleContinue}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
export default AddressInputScreen;
const styleSet = createStyleSheets(() => ({
    keyboardAvoidingView: {
        flex: 1,
    },
}));
